export const GLADLY = {
  IDENTITY_TYPE: {
    EMAIL: 'EMAIL',
    MOBILE_PHONE_NUMBER: 'MOBILE_PHONE_NUMBER',
  },
};

/**
 * @name safelySetGladlyUser
 * @description Safely sets the Gladly user in the widget using the provided user object.
 *              If a user is already set or no valid identity (email/phone) exists, it does nothing.
 * @type {public}
 * @args {Object} user - User object containing firstName, lastName, email, and/or phone.
 * @returns {void}
 */
export function safelySetGladlyUser(user) {
  if (!user || !window?.Gladly?.setUser) return;
  if (window.Gladly.getUser?.()) return;

  const gladlyUser = {
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim().replace(/\s+/g, ' '),
  };

  if (user.email) {
    gladlyUser.email = user.email;
    gladlyUser.identityType = GLADLY.IDENTITY_TYPE.EMAIL;
  } else if (user.phone) {
    gladlyUser.phone = user.phone;
    gladlyUser.identityType = GLADLY.IDENTITY_TYPE.MOBILE_PHONE_NUMBER;
  } else {
    return; // ✅ Do not proceed without email or phone
  }

  window.Gladly.clearUser?.();
  window.Gladly.setUser(gladlyUser);
}

/**
 * @name initGladly
 * @description Initializes the Gladly chat widget with the provided appId.
 *              Adds support for persisting the chat launcher visibility across sessions.
 * @type {public}
 * @args {string} appId - The Gladly application ID used to initialize the widget.
 * @returns {Promise<void>} A promise that resolves when initialization is complete.
 */
export function initGladly(appId) {
  if (!window?.Gladly?.init) return Promise.resolve();

  return window.Gladly
    .init({ appId, autoShowButton: true })
    .then(() => {
      const wasOpened = sessionStorage.getItem('gladlyChatIsOpenedOnce') === 'true';
      const chatContainer = document.getElementById('gladlyChat_container');
      if (wasOpened && chatContainer) chatContainer.classList.add('wasOpenedOnce');

      window.Gladly.on?.('sidekick:opened', () => sessionStorage.setItem('gladlyChatIsOpenedOnce', 'true'));
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Gladly init error:', err);
    });
}
